<template>
  <div class="home">
    <home-hero />
    <template v-if="tags.length > 0">
      <home-tag
        :key="tags[0].node.uuid"
        :tag="tags[0].node"
      />
      <div class="container">
        <div class="home__ad">
          <div class="home__ad-inner">
            <p class="home__ad-title">{{ t('home.banner1.title') }}</p>
            <p class="home__ad-text">{{ t('home.banner1.text') }}</p>
            <router-link :to="translations.i18nRoute({ name: 'store' })" class="home__ad-button">
              {{ t('home.banner1.button') }}
              <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.30957 12.5H19.3096" stroke="#FF6162" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12.3096 5.5L19.3096 12.5L12.3096 19.5" stroke="#FF6162" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </router-link>
          </div>
          <img src="@images/homeBanner1.png" alt="banner" loading="lazy">
        </div>
      </div>
      <home-tag
        v-for="tag in tags.slice(1)"
        :key="tag.node.uuid"
        :tag="tag.node"
      />
    </template>
    <home-tag-skeleton
        v-for="idx in 3"
        :key="idx"
        v-else
    />
  </div>
</template>

<script setup>
import HomeHero from "@/components/home/home-hero.vue";
import {computed, onMounted, ref, watch} from "vue";
import {Products} from "@/core/api/products.js";
import {ElNotification} from "element-plus";
import {useI18n} from "vue-i18n";
import {useRoute} from "vue-router";
import {Auth} from "@/core/api/auth.js";
import HomeTag from "@/components/home/home-tag.vue";
import HomeTagSkeleton from "@/components/skeletons/home-tag-skeleton.vue";
import translations from "@/core/helpers/translations.js";

const { t, locale } = useI18n()
const route = useRoute()

const token = computed(() =>
  route.query.token ? (route.query.token) : undefined,
);
const uid = computed(() =>
  route.query.uid ? (route.query.uid) : undefined,
);

const tags = ref([])

const activateAccount = async () => {
  if (token.value && uid.value && route.name === "activate-user") {
    const res = await Auth.activateUser(token.value, uid.value)

    if (res.data.activateUser) {
      ElNotification({
        message: t("popup.activationSuccess"),
        type: "success"
      });
    } else {
      ElNotification({
        title: t("popup.error"),
        message: res.errors[0].message,
        type: "error"
      });
    }
  }
};

watch(locale, async () => {
  tags.value = (await Products.getProductTags()).data.productTags.edges
})

onMounted(async () => {
  if (route.name === "payment") {
    ElNotification({
      message: t('popup.payment'),
      type: 'success'
    })
  }

  tags.value = (await Products.getProductTags()).data.productTags.edges

  await activateAccount();
});
</script>

<style lang="scss" scoped>
.home {
  &__ad {
    margin-top: 50px;
    border-radius: 20px;
    background-color: #F6F7F8;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 150px;
    padding-bottom: 25px;

    &-inner {
      padding: 35px 0 35px 50px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 20px;
    }

    &-title {
      color: #2F2F2F;
      font-size: 40px;
      font-weight: 600;
      line-height: 48px;
    }

    &-text {
      color: #5A5A5A;
      font-family: "Readex Pro", sans-serif;
      font-size: 18px;
      font-weight: 300;
      line-height: 25px;
    }

    &-button {
      cursor: pointer;
      background-color: transparent;
      display: flex;
      align-items: center;
      gap: 5px;

      font-size: 16px;
      font-weight: 600;
      color: #FF6162;

      @include hover {
        & svg {
          transform: rotate(360deg);
        }
      }

      & svg {
        transition: 0.2s;
      }
    }
  }
}

@media (max-width: 1300px) {
  .home {
    &__ad {
      gap: 50px;

      & img {
        width: 400px;
      }
    }
  }
}

@media (max-width: 950px) {
  .home {
    &__ad {
      &-inner {
        padding: 20px 0 20px 30px;
      }

      &-title {
        font-size: 32px;
        line-height: 36px;
      }

      &-text {
        font-size: 14px;
        line-height: 18px;
      }
    }
  }
}

@media (max-width: 700px) {
  .home {
    &__ad {
      flex-direction: column-reverse;
      align-items: flex-end;
      gap: 20px;

      &-inner {
        padding: 20px 30px;
      }
    }
  }
}

@media (max-width: 450px) {
  .home {
    &__ad {
      & img {
        width: 100%;
      }
    }
  }
}
</style>